export type BrainQuality = 'vermin' | 'cat' | 'dog' | 'human';

const QUALITY_MULTIPLIERS: Record<BrainQuality, number> = {
  vermin: 0.005,
  cat: 0.02,
  dog: 0.03,
  human: 0.5,
};

const ANIMAL_ASYMPTOTE = 0.5;

const DEFAULT_THRESHOLDS: readonly number[] = [0, 0.1, 0.3, 0.4, 0.9];

export interface BrainSystemOptions {
  initialBrain?: number;
  max?: number;
  thresholds?: readonly number[];
}

export type BrainListener = (value: number, level: number) => void;

export class BrainSystem {
  private brain: number;
  private readonly max: number;
  private readonly thresholds: readonly number[];
  private listeners: BrainListener[] = [];
  private humanConsumed = false;

  constructor(options: BrainSystemOptions = {}) {
    this.brain = options.initialBrain ?? 0;
    this.max = options.max ?? 1;
    this.thresholds = options.thresholds ?? DEFAULT_THRESHOLDS;
  }

  consume(quality: BrainQuality, mass: number): void {
    if (mass <= 0) return;
    const gain = QUALITY_MULTIPLIERS[quality] * mass;
    const previousLevel = this.currentLevel;
    if (quality === 'human') {
      this.humanConsumed = true;
    }
    const cap = this.humanConsumed ? this.max : Math.min(ANIMAL_ASYMPTOTE, this.max);
    this.brain = Math.min(cap, this.brain + gain);
    const newLevel = this.currentLevel;
    if (newLevel !== previousLevel) {
      this.emit(newLevel);
    }
  }

  /** Direct state replace — used by save-restore. */
  setState(input: { value: number; humanConsumed: boolean }): void {
    const previousLevel = this.currentLevel;
    this.humanConsumed = input.humanConsumed;
    const cap = this.humanConsumed ? this.max : Math.min(ANIMAL_ASYMPTOTE, this.max);
    this.brain = Math.max(0, Math.min(cap, input.value));
    const newLevel = this.currentLevel;
    if (newLevel !== previousLevel) {
      this.emit(newLevel);
    }
  }

  get currentValue(): number {
    return this.brain;
  }

  get currentLevel(): number {
    for (let i = this.thresholds.length - 1; i >= 0; i--) {
      if (this.brain >= this.thresholds[i]) return i;
    }
    return 0;
  }

  get humanBrainConsumed(): boolean {
    return this.humanConsumed;
  }

  onLevelChange(listener: BrainListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private emit(level: number): void {
    for (const listener of this.listeners) {
      listener(this.brain, level);
    }
  }
}
